import { useMemo } from 'react'
import ScrollSnapWheel from './ScrollSnapWheel'
import { SpecChargeCyclesIcon } from './ListingSpecIcons'

const DEFAULT_MAX = 1200
const DEFAULT_SCROLL = 347
export const BATTERY_CYCLES_INPUT_MAX = 9999

export function sanitizeBatteryCyclesInput(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (!digits) return ''
  const n = parseInt(digits, 10)
  if (!Number.isFinite(n)) return ''
  return String(Math.min(n, BATTERY_CYCLES_INPUT_MAX))
}

function cyclesKnown(value: string): boolean {
  const trimmed = value.trim()
  const parsed = parseInt(trimmed, 10)
  return trimmed !== '' && Number.isFinite(parsed) && parsed >= 0
}

type ChipProps = {
  value: string
  onChange: (value: string) => void
  ariaLabel?: string
}

/** Editable pill — same slot as battery `listing-spec-value-chip` in builder spec cells. */
const CYCLES_ACCENT = '#48a0b8'

export function BatteryCyclesValueChip({
  value,
  onChange,
  ariaLabel = 'Charge cycles',
}: ChipProps) {
  const known = cyclesKnown(value)
  const tone = known ? CYCLES_ACCENT : '#94a3b8'
  return (
    <span
      className={`listing-spec-value-chip listing-spec-value-chip--battery battery-cycles-chip-shell${
        known ? '' : ' listing-spec-value-chip--muted battery-cycles-chip-shell--empty'
      }`}
      style={known ? { color: tone, borderColor: `${tone}55` } : undefined}
    >
      <SpecChargeCyclesIcon size={18} color={tone} />
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className="battery-cycles-chip-input"
        value={value}
        maxLength={4}
        placeholder=""
        onChange={(e) => onChange(sanitizeBatteryCyclesInput(e.target.value))}
        aria-label={ariaLabel}
      />
    </span>
  )
}

interface BatteryCyclesWheelPickerProps {
  value: string
  onChange: (value: string) => void
  max?: number
  compact?: boolean
  ariaLabel?: string
  /** When false, chip lives in `ListingSpecCell` valueChip (builder row). */
  showValueChip?: boolean
}

export default function BatteryCyclesWheelPicker({
  value,
  onChange,
  max = DEFAULT_MAX,
  compact = false,
  ariaLabel = 'Charge cycles',
  showValueChip = true,
}: BatteryCyclesWheelPickerProps) {
  const trimmed = value.trim()
  const parsed = parseInt(trimmed, 10)
  const known = cyclesKnown(value)

  const items = useMemo(() => {
    const ceiling = known && parsed > max ? parsed : max
    const nums: number[] = []
    for (let n = ceiling; n >= 0; n -= 1) nums.push(n)
    return nums
  }, [known, max, parsed])

  const wheelValue = useMemo(() => {
    if (known) return Math.min(parsed, items[0] ?? max)
    const fallback = items.includes(DEFAULT_SCROLL) ? DEFAULT_SCROLL : items[0] ?? 0
    return fallback
  }, [items, known, max, parsed])

  return (
    <div className={`battery-scroll-field battery-cycles-scroll-field${compact ? ' battery-scroll-field--compact' : ''}`}>
      {showValueChip ? (
        <div className="listing-spec-cell__value battery-cycles-chip-wrap">
          <BatteryCyclesValueChip value={value} onChange={onChange} ariaLabel={ariaLabel} />
        </div>
      ) : null}
      <ScrollSnapWheel
        className="battery-scroll-wheel cycles-scroll-wheel"
        value={wheelValue}
        onChange={(n) => onChange(String(n))}
        items={items}
        ariaLabel={ariaLabel}
        highlightActive={known}
        compact={compact}
      />
    </div>
  )
}
